import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    InternalAxiosRequestConfig,
} from "axios";
import { errorMessage } from "./errorMessage";
import { getCookieCsrfHeaders } from "./csrf";
import { usesNativeRefreshOwnership } from "./nativeBridge";
import {
    NativeRefreshBrokerError,
    requestNativeRefreshAccess,
    supportsNativeRefreshBroker,
    waitForNativeRefreshBrokerCapability,
} from "./nativeRefreshBroker";

/** ENV */
const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "";
const CUSTOM_URL =
    process.env.REACT_APP_CUSTOM_API_BASE_URL || "";
const IDP_ORIGIN =
    process.env.REACT_APP_IDP_ORIGIN || "";
const IDP_REFRESH_PATH = "/api/refresh";
const IDP_ROLES_REFRESH_PATH = "/api/roles/refresh";
export const BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 = "cookie-only-v1";
export const NATIVE_SESSION_MODE_HEADER = "X-Native-Session-Mode";
export const NATIVE_SESSION_MODE_HOST_ONLY_V1 = "host-only-v1";

/** UNAUTHORIZED REDIRECT */
const ID_LOGIN_URL =
    process.env.REACT_APP_ID_LOGIN_URL ||
    (IDP_ORIGIN ? `${IDP_ORIGIN.replace(/\/$/, "")}/login` : "/login");
const UNAUTHORIZED_REDIRECT_URL =
    process.env.REACT_APP_UNAUTHORIZED_REDIRECT_URL || ID_LOGIN_URL;

function buildUnauthorizedRedirectUrl(): string {
    if (typeof window === "undefined") {
        return UNAUTHORIZED_REDIRECT_URL;
    }

    try {
        const target = new URL(UNAUTHORIZED_REDIRECT_URL, window.location.origin);
        if (!target.searchParams.has("returnTo")) {
            target.searchParams.set("returnTo", window.location.href);
        }
        return target.toString();
    } catch {
        return UNAUTHORIZED_REDIRECT_URL;
    }
}

export function redirectOnUnauthorized(): void {
    if (typeof window !== "undefined") {
        window.location.replace(buildUnauthorizedRedirectUrl());
    }
}

export function redirectToLogin(): never {
    redirectOnUnauthorized();
    throw new Error("Unauthorized");
}

/** LS */
const ACTIVE_COMPANY_KEY = "activeCompanyId";
const ACTIVE_COMPANY_KEY_API = "activeCompanyIdApi";
export const ACTIVE_COMPANY_EVENT = "chio:active-company-change";
export const ACTIVE_COMPANY_API_EVENT = "chio:active-company-api-change";

function emitActiveCompanyEvent(id: string | null) {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent(ACTIVE_COMPANY_EVENT, {
        detail: { id },
    }));
}

function emitActiveCompanyApiEvent(id: string | null) {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent(ACTIVE_COMPANY_API_EVENT, {
        detail: { id },
    }));
}

/** helpers company */
export function getActiveCompanyId(): string | null {
    return localStorage.getItem(ACTIVE_COMPANY_KEY);
}
export function setActiveCompanyId(id: string | number | null) {
    if (id === null || id === undefined) {
        localStorage.removeItem(ACTIVE_COMPANY_KEY);
        emitActiveCompanyEvent(null);
    } else {
        const next = String(id);
        localStorage.setItem(ACTIVE_COMPANY_KEY, next);
        emitActiveCompanyEvent(next);
    }
}
export function getActiveCompanyIdApi(): string | null {
    return localStorage.getItem(ACTIVE_COMPANY_KEY_API);
}
export function setActiveCompanyIdApi(id: string | number | null) {
    if (id === null || id === undefined) {
        localStorage.removeItem(ACTIVE_COMPANY_KEY_API);
        emitActiveCompanyApiEvent(null);
        return;
    }
    const next = String(id);
    localStorage.setItem(ACTIVE_COMPANY_KEY_API, next);
    emitActiveCompanyApiEvent(next);
}

/** in-memory access token */
const ACCESS_TOKEN_STORAGE_KEY = "access_token";
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
const ACCESS_TOKEN_COOKIE_KEY = "access_token_cookie";
const FP_STORAGE_KEY = "csrf_fp";
const COOKIE_SESSION_FRESH_UNTIL_KEY = "cookie_session_fresh_until";
const COOKIE_SESSION_FRESH_MS = 10 * 60 * 1000;
const JWT_PART_PATTERN = /^[A-Za-z0-9_-]+$/;

type NativeBridgeRefreshExport = {
    access_token?: string;
    refresh_token?: string;
    fp?: string;
    src?: string;
};

let accessToken: string | null = getStoredAccessToken();
let accessExp = accessToken ? decodeExp(accessToken) : 0;

function setAccessToken(token: string | null) {
    const normalized = normalizeAccessToken(token ?? "");
    accessToken = normalized || null;
    accessExp = normalized ? decodeExp(normalized) : 0;
    persistAccessToken(normalized);
}

function decodeExp(jwt: string): number {
    try {
        const [, p] = jwt.split(".");
        const json = JSON.parse(atob(p.replace(/-/g, "+").replace(/_/g, "/")));
        return typeof json.exp === "number" ? json.exp : 0;
    } catch {
        return 0;
    }
}

function readSessionStorageValue(key: string): string {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return "";
        }

        return window.sessionStorage.getItem(key) || "";
    } catch {
        return "";
    }
}

function writeSessionStorageValue(key: string, value: string): void {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return;
        }

        if (value) {
            window.sessionStorage.setItem(key, value);
        } else {
            window.sessionStorage.removeItem(key);
        }
    } catch {
        // ignore
    }
}

function isCookieSessionFresh(): boolean {
    const freshUntil = Number(readSessionStorageValue(COOKIE_SESSION_FRESH_UNTIL_KEY));

    return Number.isFinite(freshUntil) && freshUntil > Date.now();
}

function markCookieSessionFresh(): void {
    writeSessionStorageValue(
        COOKIE_SESSION_FRESH_UNTIL_KEY,
        String(Date.now() + COOKIE_SESSION_FRESH_MS)
    );
}

function clearCookieSessionFresh(): void {
    writeSessionStorageValue(COOKIE_SESSION_FRESH_UNTIL_KEY, "");
}

function readLocalStorageValue(key: string): string {
    try {
        if (typeof window === "undefined" || !window.localStorage) {
            return "";
        }

        return window.localStorage.getItem(key) || "";
    } catch {
        return "";
    }
}

function normalizeAccessToken(token: string): string {
    const normalized = token
        .trim()
        .replace(/^Bearer\s+/i, "")
        .replace(/^['"]+|['"]+$/g, "");
    const parts = normalized.split(".");

    return parts.length === 3 && parts.every((part) => part.length > 0 && JWT_PART_PATTERN.test(part))
        ? normalized
        : "";
}

function normalizeRefreshToken(token: string): string {
    return token.trim().replace(/^['"]+|['"]+$/g, "");
}

function persistAccessToken(token: string): void {
    writeSessionStorageValue(
        ACCESS_TOKEN_STORAGE_KEY,
        isInWebView() ? normalizeAccessToken(token) : ""
    );
}

function persistRefreshToken(token: string): void {
    writeSessionStorageValue(
        REFRESH_TOKEN_STORAGE_KEY,
        isInWebView() ? normalizeRefreshToken(token) : ""
    );
}

function isInWebView(): boolean {
    try {
        if (typeof window === "undefined") {
            return false;
        }

        const w = window as any;
        if (w.ReactNativeWebView || w.__CC_NATIVE_CAPABILITIES__ || w.__CC_NATIVE_SESSION_CONTEXT__) {
            return true;
        }

        const ua = String(window.navigator?.userAgent || "").toLowerCase();
        return (
            ua.includes("; wv") ||
            /\bwv\b/.test(ua) ||
            ((ua.includes("iphone") || ua.includes("ipad")) && ua.includes("mobile") && !ua.includes("safari"))
        );
    } catch {
        return false;
    }
}

export function getNativeSessionModeHeaders(): Record<string, string> {
    try {
        const capabilities = (window as any).__CC_NATIVE_CAPABILITIES__;
        return capabilities?.nativeSessionMode === NATIVE_SESSION_MODE_HOST_ONLY_V1
            ? { [NATIVE_SESSION_MODE_HEADER]: NATIVE_SESSION_MODE_HOST_ONLY_V1 }
            : {};
    } catch {
        return {};
    }
}

export function getBrowserSessionProtocolPayload(): Record<string, string> {
    return isInWebView()
        ? {}
        : { session_protocol: BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 };
}

function getNativeSessionEpoch(): number | undefined {
    try {
        const raw = (window as any).__CC_NATIVE_SESSION_CONTEXT__?.sessionEpoch;
        return typeof raw === "number" && Number.isFinite(raw) && raw >= 0
            ? Math.floor(raw)
            : undefined;
    } catch {
        return undefined;
    }
}

function getStoredAccessToken(): string | null {
    if (!isInWebView()) {
        writeSessionStorageValue(ACCESS_TOKEN_STORAGE_KEY, "");
        writeSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY, "");
        return null;
    }

    const cookieToken = normalizeAccessToken(getCookie(ACCESS_TOKEN_COOKIE_KEY) || "");
    if (cookieToken) {
        persistAccessToken(cookieToken);
        return cookieToken;
    }

    const storedToken = readSessionStorageValue(ACCESS_TOKEN_STORAGE_KEY);
    const normalized = normalizeAccessToken(storedToken);
    if (!normalized && storedToken) {
        writeSessionStorageValue(ACCESS_TOKEN_STORAGE_KEY, "");
    }

    return normalized || null;
}

function getStoredRefreshToken(): string {
    if (usesNativeRefreshOwnership()) return "";
    if (!isInWebView()) {
        writeSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY, "");
        return "";
    }

    const storedToken = normalizeRefreshToken(readSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY));
    if (storedToken) {
        return storedToken;
    }

    const cookieToken = normalizeRefreshToken(getCookie(REFRESH_TOKEN_STORAGE_KEY) || "");
    if (cookieToken) {
        persistRefreshToken(cookieToken);
        return cookieToken;
    }

    if (!storedToken && readSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY)) {
        writeSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY, "");
    }

    return storedToken;
}

function postToNative(type: "refresh_export_inline", data: NativeBridgeRefreshExport): boolean {
    try {
        if (!isInWebView()) {
            return false;
        }

        const bridge = (window as any).ReactNativeWebView;
        if (!bridge || typeof bridge.postMessage !== "function") {
            return false;
        }

        const epoch = getNativeSessionEpoch();
        bridge.postMessage(JSON.stringify({
            v: 1,
            type,
            data,
            ...(epoch == null ? {} : { epoch }),
        }));
        return true;
    } catch {
        return false;
    }
}

function exportSessionToNative(data: NativeBridgeRefreshExport): void {
    if (!data.access_token && !data.refresh_token) {
        return;
    }

    postToNative("refresh_export_inline", data);
}

function isTokenExpiredSoon(): boolean {
    if (!accessToken || !accessExp) return true;
    return accessExp * 1000 - Date.now() < 30000;
}

function shouldRefreshBeforeRequest(eagerRefreshOnMissingToken: boolean): boolean {
    if (accessToken) {
        return isTokenExpiredSoon();
    }

    return eagerRefreshOnMissingToken;
}

/** IdP клиент для refresh */
const idp: AxiosInstance = axios.create({
    baseURL: IDP_ORIGIN,
    withCredentials: true,
    timeout: 10000,
});

function shouldUseJsonContentType(method?: string): boolean {
    switch ((method ?? "get").toLowerCase()) {
    case "post":
    case "put":
    case "patch":
        return true;
    default:
        return false;
    }
}

function isFormDataPayload(data: unknown): boolean {
    return typeof FormData !== "undefined" && data instanceof FormData;
}

function removeHeader(headers: any, name: string) {
    if (!headers) return;

    if (typeof headers.delete === "function") {
        headers.delete(name);
        return;
    }

    delete headers[name];
    delete headers[name.toLowerCase()];
}

/** fingerprint из cookie домена .chio-chio.ru (не HttpOnly) */
function getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = document.cookie.match(
        new RegExp("(?:^|; )" + escaped + "=([^;]*)")
    );
    return m ? decodeURIComponent(m[1]) : null;
}
function getFp(): string | null {
    return getCookie("fp") || readLocalStorageValue(FP_STORAGE_KEY) || null;
}

/** единый refresh */
let refreshing = false;
export type SessionRecoveryResult = "refreshed" | "unauthorized" | "unavailable";
let waiters: Array<(result: SessionRecoveryResult) => void> = [];

type RefreshResponse = {
    access_token?: string;
    token?: string;
    refresh_token?: string;
    status?: string;
    error?: string;
};

async function refreshSession(force = false): Promise<SessionRecoveryResult> {
    if (!force) {
        if (!isTokenExpiredSoon()) return "refreshed";
        if (!accessToken && isCookieSessionFresh()) return "refreshed";
    }
    if (refreshing) return new Promise<SessionRecoveryResult>((res) => waiters.push(res));
    refreshing = true;
    let result: SessionRecoveryResult = "unavailable";
    try {
        if (isInWebView()) {
            // Bootstrap can arrive after module evaluation on Android. Wait
            // for native capabilities before selecting the refresh owner.
            if (!supportsNativeRefreshBroker() &&
                !(window as any).__CC_NATIVE_CAPABILITIES__ &&
                !getStoredRefreshToken()) {
                await waitForNativeRefreshBrokerCapability();
            }
            if (usesNativeRefreshOwnership()) {
                if (!supportsNativeRefreshBroker()) return result;
                const native = await requestNativeRefreshAccess();
                const token = normalizeAccessToken(native.accessToken);
                if (!token) throw new NativeRefreshBrokerError("refresh_failed");
                setAccessToken(token);
                clearCookieSessionFresh();
                result = "refreshed";
                return result;
            }
        }
        const fp = getFp();
        const rt = getStoredRefreshToken();
        const headers: Record<string, string> = {};
        if (fp) headers["X-CSRF-Token"] = fp;
        if (rt) headers["X-Refresh-Token"] = rt;
        if (isInWebView()) headers["X-Client"] = "mobile";
        Object.assign(headers, getNativeSessionModeHeaders());
        const refreshPayload = rt
            ? { refresh_token: rt }
            : getBrowserSessionProtocolPayload();

        const res = await idp.post<RefreshResponse>(
            IDP_REFRESH_PATH,
            Object.keys(refreshPayload).length ? refreshPayload : undefined,
            {
                headers: Object.keys(headers).length ? headers : undefined,
                withCredentials: true,
            }
        );
        if (res.data?.error) {
            setAccessToken(null);
            clearCookieSessionFresh();
            // A malformed successful response is not proof of an expired session.
            return result;
        }

        const t: string | undefined = res.data?.access_token || res.data?.token;
        if (!t) {
            setAccessToken(null);
            // Browser cookie flow intentionally receives no token in the body:
            // the IdP rotated the HttpOnly access cookie and a retry can use it.
            // Explicit native refresh-token flow must still receive a token.
            if (!rt) {
                result = "refreshed";
                markCookieSessionFresh();
            } else {
                clearCookieSessionFresh();
            }
            return result;
        }
        setAccessToken(t);
        clearCookieSessionFresh();
        if (typeof res.data?.refresh_token === "string" && res.data.refresh_token) {
            persistRefreshToken(res.data.refresh_token);
        }
        exportSessionToNative({
            access_token: t,
            refresh_token: typeof res.data?.refresh_token === "string" ? res.data.refresh_token : rt,
            fp: fp || "",
            src: "lkfr_refresh",
        });
        result = "refreshed";
        return result;
    } catch (error) {
        const invalidNativeSession = error instanceof NativeRefreshBrokerError &&
            ["invalid_or_expired", "token_reused", "fingerprint_mismatch", "session_unavailable"].includes(error.code);
        if (invalidNativeSession || (error as AxiosError)?.response?.status === 401) {
            result = "unauthorized";
            setAccessToken(null);
            clearCookieSessionFresh();
        }
        // Preserve the session during a timeout, lost network or IdP outage.
        return result;
    } finally {
        refreshing = false;
        waiters.splice(0).forEach((fn) => fn(result));
    }
}

async function refreshOnce(force = false): Promise<boolean> {
    return (await refreshSession(force)) === "refreshed";
}

export function recoverApiSession(): Promise<SessionRecoveryResult> {
    return refreshSession(true);
}

export type RefreshRolesResponse = {
    access_token?: string;
    token?: string;
    roles?: string[];
    user_id?: number;
    error?: string;
};

export async function refreshRolesJwt(): Promise<RefreshRolesResponse> {
    await refreshOnce(false);

    const fp = getFp();
    const headers: Record<string, string> = {};
    if (fp) headers["X-CSRF-Token"] = fp;
    if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
    if (isInWebView()) headers["X-Client"] = "mobile";
    Object.assign(headers, getNativeSessionModeHeaders());
    const rolesPayload = getBrowserSessionProtocolPayload();

    const res = await idp.post<RefreshRolesResponse>(
        IDP_ROLES_REFRESH_PATH,
        Object.keys(rolesPayload).length ? rolesPayload : undefined,
        {
            headers,
            withCredentials: true,
        }
    );

    const token: string | undefined = res.data?.access_token || res.data?.token;
    if (token) {
        setAccessToken(token);
        exportSessionToNative({
            access_token: token,
            refresh_token: getStoredRefreshToken(),
            fp: fp || "",
            src: "lkfr_roles_refresh",
        });
    }

    return res.data;
}

/** http фабрика с установкой Authorization на каждый запрос */
function makeHttp(
    baseURL: string,
    companyKey: string | null,
    options?: {
        includeCompanyHeader?: boolean;
        useAuthorizationHeader?: boolean;
        useNativeBrokerAuthorizationHeader?: boolean;
        eagerRefreshOnMissingToken?: boolean;
        includeCookieCsrf?: boolean;
    }
) {
    const includeCompanyHeader = options?.includeCompanyHeader ?? true;
    const useAuthorizationHeader = options?.useAuthorizationHeader ?? true;
    const shouldUseAuthorizationHeader = () => useAuthorizationHeader || Boolean(
        options?.useNativeBrokerAuthorizationHeader && isInWebView() && usesNativeRefreshOwnership()
    );
    const eagerRefreshOnMissingToken = options?.eagerRefreshOnMissingToken ?? true;
    const includeCookieCsrf = options?.includeCookieCsrf ?? false;
    const http = axios.create({
        baseURL,
        withCredentials: true,
        timeout: 15000,
    });

    http.interceptors.request.use(async (cfg: InternalAxiosRequestConfig) => {
        const selectedCompany = companyKey ? localStorage.getItem(companyKey) : null;
        if (shouldRefreshBeforeRequest(eagerRefreshOnMissingToken)) {
            await refreshOnce(false);
        }

        cfg.headers = cfg.headers ?? {};

        if (isFormDataPayload(cfg.data)) {
            removeHeader(cfg.headers, "Content-Type");
        } else if (shouldUseJsonContentType(cfg.method)) {
            (cfg.headers as any)["Content-Type"] = "application/json";
        } else {
            removeHeader(cfg.headers, "Content-Type");
        }

        if (!shouldUseAuthorizationHeader()) {
            delete (cfg.headers as any)["Authorization"];
        } else if (accessToken) {
            (cfg.headers as any)["Authorization"] = `Bearer ${accessToken}`;
        } else {
            delete (cfg.headers as any)["Authorization"];
        }

        if (includeCookieCsrf) {
            Object.assign(cfg.headers as any, getCookieCsrfHeaders(cfg.method));
        }

        if (includeCompanyHeader && companyKey) {
            const existingCompany = (cfg.headers as any).get?.("X-Company-Id") || (cfg.headers as any)["X-Company-Id"];
            if (selectedCompany && !existingCompany) (cfg.headers as any)["X-Company-Id"] = String(selectedCompany);
        }

        return cfg;
    });

    http.interceptors.response.use(
        (r) => r,
        async (error: AxiosError) => {
            const cfg = error.config as
                | (InternalAxiosRequestConfig & { _retried?: boolean })
                | undefined;
            const status = error.response?.status;

            if (status === 401 && cfg && !cfg._retried) {
                cfg._retried = true;
                const recovery = await refreshSession(true);
                if (recovery === "unavailable") {
                    throw new SessionCheckUnavailableError("Сервис авторизации временно не отвечает. Повторите попытку.");
                }
                if (recovery === "refreshed") {
                    // A timed-out write may already have reached the server. Only
                    // repeat reads automatically after renewing the session.
                    if (!/^(get|head|options)$/i.test(cfg.method ?? "")) {
                        const retryError = new Error("Сессия обновлена. Повторите действие.");
                        (retryError as any).status = 401;
                        throw retryError;
                    }
                    cfg.headers = cfg.headers ?? {};
                    if (!shouldUseAuthorizationHeader()) {
                        delete (cfg.headers as any)["Authorization"];
                    } else if (accessToken)
                        (cfg.headers as any)["Authorization"] = `Bearer ${accessToken}`;
                    else delete (cfg.headers as any)["Authorization"];
                    return http.request(cfg);
                }
                redirectToLogin();
            }

            if (status === 401) {
                redirectToLogin();
            }

            const details = error.response?.data;
            const err = new Error(errorMessage({ status, details, message: error.message }));
            (err as any).status = status;
            (err as any).details = details;
            (err as any).transient = !status;
            throw err;
        }
    );

    return http;
}

function resolveBaseUrl(baseURL: string, path: string): string {
    if (/^https?:\/\//i.test(path)) {
        return path;
    }

    const normalizedBase = baseURL.replace(/\/$/, "");
    if (!normalizedBase) {
        return path;
    }

    return normalizedBase + (path.startsWith("/") ? path : "/" + path);
}

async function buildAuthorizedFetchRequest(
    baseURL: string,
    companyKey: string,
    eagerRefreshOnMissingToken: boolean,
    path: string,
    init?: RequestInit
): Promise<{ url: string; init: RequestInit }> {
    if (shouldRefreshBeforeRequest(eagerRefreshOnMissingToken)) {
        await refreshOnce(false);
    }

    const headers = new Headers(init?.headers ?? {});
    if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const csrfHeaders = getCookieCsrfHeaders(init?.method);
    if (csrfHeaders["X-CSRF-Token"]) {
        headers.set("X-CSRF-Token", csrfHeaders["X-CSRF-Token"]);
    }

    const companyId = localStorage.getItem(companyKey);
    if (companyId) {
        headers.set("X-Company-Id", String(companyId));
    }

    return {
        url: resolveBaseUrl(baseURL, path),
        init: {
            ...init,
            headers,
            credentials: "include",
        },
    };
}

/** клиенты */
export const http = makeHttp(BASE_URL, ACTIVE_COMPANY_KEY, {
    eagerRefreshOnMissingToken: false,
    includeCookieCsrf: true,
});
export const httpCustom = makeHttp(CUSTOM_URL, ACTIVE_COMPANY_KEY_API);

// The personal assistant has an explicit salon selector. Do not overwrite its
// selected address with another page's localStorage selection during refresh.
const assistantHttp = makeHttp(BASE_URL, null, { includeCompanyHeader: false, includeCookieCsrf: true, eagerRefreshOnMissingToken: false });
export async function askAssistant<T>(companyId: string, body: { question: string; from: string; to: string; compareCompanyIds?: number[]; contextToken?: string; conversationId?: string; conversationVersion?: number }, signal: AbortSignal): Promise<T> {
    try {
        const response = await assistantHttp.post<T>("/api/assistant/chat", body, {
            headers: { "X-Company-Id": companyId }, signal, timeout: 240000,
        });
        return response.data;
    } catch (e) { return normalizeAxiosError(e); }
}

/** обёртки */
type ApiGetConfig = Omit<AxiosRequestConfig, "params">;

function normalizeAxiosError(e: unknown): never {
    const ax = e as AxiosError<any>;
    const existing = e as any;

    if (e instanceof SessionCheckUnavailableError) {
        throw e;
    }

    if (!ax.response && (existing?.details !== undefined || existing?.status !== undefined)) {
        throw existing;
    }

    const status = ax.response?.status;
    const details = ax.response?.data;

    if (status === 401) {
        redirectToLogin();
    }

    const err = new Error(errorMessage({ status, details, message: ax.message }));
    (err as any).status = status;
    (err as any).details = details;
    (err as any).transient = !status;
    throw err;
}

export const api = {
    async get<T>(path: string, params?: Record<string, any>, config?: ApiGetConfig) {
        try {
            const res = await http.get<T>(path, { ...config, params });
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async post<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await http.post<T>(path, body ?? undefined, config);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async put<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await http.put<T>(path, body ?? undefined, config);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async patch<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await http.patch<T>(path, body ?? undefined, config);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async delete<T>(path: string) {
        try {
            const res = await http.delete<T>(path);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
};

export const apiCustom = {
    async get<T>(path: string, params?: Record<string, any>, config?: ApiGetConfig) {
        try {
            const res = await httpCustom.get<T>(path, { ...config, params });
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async post<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await httpCustom.post<T>(path, body ?? undefined, config);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async put<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await httpCustom.put<T>(path, body ?? undefined, config);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async patch<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await httpCustom.patch<T>(path, body ?? undefined, config);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async delete<T>(path: string) {
        try {
            const res = await httpCustom.delete<T>(path);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
};

export async function ensureWarmup(): Promise<void> {
    await refreshOnce(false);
}

export function resolveApiUrl(path: string): string {
    return resolveBaseUrl(BASE_URL, path);
}

export async function buildApiFetchRequest(path: string, init?: RequestInit): Promise<{ url: string; init: RequestInit }> {
    return buildAuthorizedFetchRequest(BASE_URL, ACTIVE_COMPANY_KEY, false, path, init);
}

const SESSION_CHECK_TIMEOUT_MS = 8000;

export class SessionCheckUnavailableError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "SessionCheckUnavailableError";
        Object.setPrototypeOf(this, SessionCheckUnavailableError.prototype);
    }
}

type SessionStatus = { ok: boolean; status: number };

async function fetchSessionStatusOnce(useAccessToken = true): Promise<SessionStatus> {
    const headers: Record<string, string> = {
        Accept: "application/json",
    };

    if (useAccessToken && accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const controller = new AbortController();
    let timeoutId: number | undefined;
    let timedOut = false;
    const timeout = new Promise<SessionStatus>((_, reject) => {
        timeoutId = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
            reject(new SessionCheckUnavailableError("session_check_timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
    });

    try {
        return await Promise.race([
            (async () => {
                const response = await fetch(resolveBaseUrl(BASE_URL, "/api/auth/me"), {
                    method: "GET",
                    credentials: "include",
                    headers,
                    signal: controller.signal,
                });
                // fetch resolves when headers arrive. A proxy can keep the body
                // open indefinitely, so the deadline must cover this read too.
                await response.arrayBuffer();
                return { ok: response.ok, status: response.status };
            })(),
            timeout,
        ]);
    } catch (error) {
        if (timedOut) throw new SessionCheckUnavailableError("session_check_timeout");
        if (error instanceof SessionCheckUnavailableError) throw error;
        throw new SessionCheckUnavailableError("session_check_network_error");
    } finally {
        window.clearTimeout(timeoutId);
    }
}

async function fetchSessionStatus(retry = true, useAccessToken = true): Promise<SessionStatus> {
    for (let attempt = 0; attempt <= Number(retry); attempt++) {
        try {
            const response = await fetchSessionStatusOnce(useAccessToken);
            if (retry && attempt === 0 && [429, 502, 503, 504].includes(response.status)) {
                continue;
            }
            return response;
        } catch (error) {
            if (retry && attempt === 0 && error instanceof SessionCheckUnavailableError) {
                continue;
            }
            throw error;
        }
    }
    throw new SessionCheckUnavailableError("session_check_unavailable");
}

function throwSessionCheckError(status: number): never {
    if (status === 403) throw new Error("HTTP 403");
    throw new SessionCheckUnavailableError(`HTTP ${status}`);
}

export async function checkSession(): Promise<boolean> {
    // Native bootstrap may arrive after this module was evaluated, or while
    // the connection error screen is visible. Re-read its current credential
    // before checking instead of retaining the first document's token.
    if (isInWebView()) {
        const nativeAccessToken = normalizeAccessToken(readSessionStorageValue(ACCESS_TOKEN_STORAGE_KEY));
        if (nativeAccessToken) setAccessToken(nativeAccessToken);
    }
    let response = await fetchSessionStatus();
    if (response.ok) {
        return true;
    }

    if (response.status !== 401) {
        throwSessionCheckError(response.status);
    }

    const recovery = await refreshSession(true);
    if (recovery === "unauthorized") {
        return false;
    }
    if (recovery === "unavailable") {
        // The refresh may have committed Set-Cookie before its body was lost,
        // or another native/WebView caller may have restored the cookie. Do
        // one read-only check without the stale bearer that would mask it.
        // Only the API's successful authentication grants access; do not
        // repeat a rotation or delete the saved native credentials.
        response = await fetchSessionStatus(false, false);
        if (response.ok) {
            setAccessToken(null);
            markCookieSessionFresh();
            return true;
        }
        if (response.status === 403) throwSessionCheckError(response.status);
        throw new SessionCheckUnavailableError("session_refresh_unavailable");
    }

    response = await fetchSessionStatus(false);
    if (response.ok) {
        return true;
    }

    if (response.status === 401) {
        return false;
    }

    throwSessionCheckError(response.status);
}

/** IDP API клиент (https://id.chio-chio.ru) */
export const httpIdp = makeHttp(IDP_ORIGIN, null, {
    includeCompanyHeader: false,
    useAuthorizationHeader: false,
    // Host-only native sessions cannot share LKFR cookies with the IdP.
    // Read the capability per request because bootstrap may arrive late.
    useNativeBrokerAuthorizationHeader: true,
    eagerRefreshOnMissingToken: false,
    includeCookieCsrf: true,
});

export const apiIdp = {
    async get<T>(path: string, params?: Record<string, any>, config?: ApiGetConfig) {
        try {
            const res = await httpIdp.get<T>(path, { ...config, params });
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async post<T>(path: string, body?: any) {
        try {
            const res = await httpIdp.post<T>(path, body ?? undefined);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async put<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await httpIdp.put<T>(path, body ?? undefined);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async patch<T>(path: string, body?: any, config?: AxiosRequestConfig) {
        try {
            const res = await httpIdp.patch<T>(path, body ?? undefined);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
    async delete<T>(path: string) {
        try {
            const res = await httpIdp.delete<T>(path);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },
};
